/**
 * PastLivesLoadingScreen — mystical loader for the Past Lives Pack run.
 *
 * Mirrors the GenerationLoadingScreen pattern (CosmosBackdrop + OrbitalLoader
 * + paidChip + phase chips + progress track). The voice is past-lives specific:
 * "Хроники Акаши открываются", "врата времени", "нити кармы" — never names
 * the model or provider (per project_astro_bot_loader_voice memory).
 *
 * Progress signals (backend-driven):
 *   - PENDING                       → indeterminate stripe, "preparing the rite"
 *   - GENERATING && cardsReady===0  → indeterminate, "the gates are opening"
 *   - GENERATING && cardsReady>0    → cardsReady/totalCards %  "{{n}} lives revealed"
 *   - READY                         → 100% (briefly shown before reveal entrance)
 */
import { useTranslation } from 'react-i18next';
import { CosmosBackdrop } from '../brand/CosmosBackdrop';
import { OrbitalLoader } from '../brand/OrbitalLoader';
import type { PastLivesRunStatus } from '../../hooks/usePastLivesRun';
import styles from './PastLivesLoadingScreen.module.css';

export interface PastLivesLoadingScreenProps {
  /** Total cards the tier promises (platinum = 13). */
  totalCards?: number;
  /** Real cards already delivered by the worker. */
  cardsReady?: number;
  /** Coarse-grained worker status. */
  status?: PastLivesRunStatus | null;
  /** Pay chip price (Telegram Stars). Omit to hide. */
  paidStars?: number;
  testId?: string;
}

export function PastLivesLoadingScreen({
  totalCards = 13,
  cardsReady = 0,
  status,
  paidStars,
  testId = 'past-lives-loading-screen',
}: PastLivesLoadingScreenProps): JSX.Element {
  const { t } = useTranslation('flows');

  const done = Math.max(0, Math.min(totalCards, cardsReady));
  const hasRealProgress = done > 0;
  const isReady = status === 'READY';
  const isGenerating = status === 'GENERATING' || hasRealProgress;

  // Chip activation (status-driven, never time-based).
  const openingDone = isGenerating || isReady;
  const renderingActive = isGenerating && !isReady;
  const renderingDone = isReady;
  const weavingActive = isReady;

  const barPct = isReady
    ? 100
    : hasRealProgress
      ? Math.round((done / totalCards) * 100)
      : isGenerating
        ? 15
        : 0;

  return (
    <div className={styles.root} data-testid={testId}>
      <CosmosBackdrop />

      <main className={styles.content}>
        <OrbitalLoader />

        {paidStars != null ? (
          <div className={styles.paidChip} role="status" aria-live="polite">
            <span aria-hidden="true">●</span>{' '}
            {t('pastLives.loader.paidChip', {
              price: paidStars,
              defaultValue: '{{price}} ⭐ paid · opening the gates',
            })}
          </div>
        ) : null}

        <h2 className={styles.title} aria-live="polite">
          {t('pastLives.loader.title.prefix', {
            defaultValue: 'The Akashic chronicles',
          })}{' '}
          <em className={styles.titleAccent}>
            {t('pastLives.loader.title.suffix', {
              count: totalCards,
              defaultValue: 'reveal your {{count}} past lives…',
            })}
          </em>
        </h2>

        <ul className={styles.progressChips} aria-live="polite">
          {/* Chip 1: opening the gates (PENDING → GENERATING) */}
          <li className={openingDone ? styles.chipDone : styles.chipActive}>
            <span aria-hidden="true">{openingDone ? '✓' : '↻'}</span>{' '}
            {t('pastLives.loader.chip.gates', {
              defaultValue: 'The gates of time creak open',
            })}
          </li>
          {/* Chip 2: portrait rendering (the long pole) */}
          <li className={renderingDone ? styles.chipDone : renderingActive ? styles.chipActive : styles.chipPending}>
            <span aria-hidden="true">{renderingDone ? '✓' : renderingActive ? '↻' : '·'}</span>{' '}
            {hasRealProgress
              ? t('pastLives.loader.chip.cardsDone', {
                  done,
                  total: totalCards,
                  defaultValue: '{{done}} of {{total}} lives unveiled',
                })
              : t('pastLives.loader.chip.painting', {
                  defaultValue: 'Painting faces across the ages',
                })}
          </li>
          {/* Chip 3: karmic weave (post-delivery polish) */}
          <li className={weavingActive ? styles.chipActive : styles.chipPending}>
            <span aria-hidden="true">{weavingActive ? '↻' : '·'}</span>{' '}
            {t('pastLives.loader.chip.weave', {
              defaultValue: 'Weaving the threads of karma',
            })}
          </li>
        </ul>

        {barPct > 0 ? (
          <div className={styles.progressTrack} aria-hidden="true">
            <div className={styles.progressFill} style={{ width: `${barPct}%` }} />
          </div>
        ) : (
          <div
            className={`${styles.progressTrack} ${styles.progressIndeterminate}`}
            aria-hidden="true"
          />
        )}

        <div className={styles.timeHint}>
          {t('pastLives.loader.timeHint', {
            defaultValue: 'You can leave — we will buzz you the moment the chronicles are ready',
          })}
        </div>
      </main>
    </div>
  );
}

export default PastLivesLoadingScreen;
